/** Pure helpers for the mortgage calculator: VA and USDA terms, closing costs, income ratios, yearly view and shareable URL state. */
import { fhaTerms } from "./fha";
import { amortize } from "./loan";
import { parsePaymentDate } from "./paymentDates";

export type LoanType = "conventional" | "fha" | "va" | "usda";
/** VA funding fee depends on use: first use, a later use, or exempt (some disabled veterans). */
export type VaUse = "first" | "subsequent" | "exempt";
export const RATES_NOTE = "2026 standard rates, confirm with your lender";
export const USDA_UPFRONT_PCT = 1;
export const USDA_ANNUAL_PCT = 0.35;

const cents = (n: number) => Math.round(n * 100) / 100;

/** VA funding fee percent (financed). Down payment is a share of the price. First use 2.15 / 1.5 / 1.25; later use 3.3 / 1.5 / 1.25; exempt 0. */
export function vaFundingPct(downPct: number, use: VaUse = "first"): number {
  if (use === "exempt") return 0;
  if (downPct >= 10) return 1.25;
  if (downPct >= 5) return 1.5;
  return use === "subsequent" ? 3.3 : 2.15;
}

export interface ProgramTerms {
  baseLoan: number; upfront: number; loan: number; ltv: number;
  upfrontPct: number; upfrontLabel: string;
  /** Annual insurance or guarantee percent of the base loan, 0 when none (VA, or conventional which is handled separately). */
  annualPct: number; insMonths: number; insName: string; minDownPct: number;
}

export function programTerms(type: LoanType, price: number, downAmt: number, years: number, vaUse: VaUse = "first"): ProgramTerms {
  const baseLoan = Math.max(0, price - downAmt);
  const ltv = price > 0 ? baseLoan / price : 0;
  const termMonths = Math.round(years * 12);
  if (type === "fha") {
    const f = fhaTerms(price, downAmt, years);
    return { baseLoan, upfront: f.upfront, loan: f.loan, ltv, upfrontPct: 1.75, upfrontLabel: "Upfront MIP (financed, 1.75%)", annualPct: f.annualMipPct, insMonths: f.mipMonths, insName: "MIP", minDownPct: 3.5 };
  }
  if (type === "va") {
    const pct = vaFundingPct(price > 0 ? (downAmt / price) * 100 : 0, vaUse);
    const upfront = cents((baseLoan * pct) / 100);
    return { baseLoan, upfront, loan: baseLoan + upfront, ltv, upfrontPct: pct, upfrontLabel: vaUse === "exempt" ? "VA funding fee (exempt)" : `VA funding fee (financed, ${pct}%, ${vaUse === "subsequent" ? "later use" : "first use"})`, annualPct: 0, insMonths: 0, insName: "", minDownPct: 0 };
  }
  if (type === "usda") {
    const upfront = cents((baseLoan * USDA_UPFRONT_PCT) / 100);
    return { baseLoan, upfront, loan: baseLoan + upfront, ltv, upfrontPct: USDA_UPFRONT_PCT, upfrontLabel: "USDA upfront guarantee fee (financed, 1%)", annualPct: USDA_ANNUAL_PCT, insMonths: termMonths, insName: "USDA annual fee", minDownPct: 0 };
  }
  return { baseLoan, upfront: 0, loan: baseLoan, ltv, upfrontPct: 0, upfrontLabel: "", annualPct: 0, insMonths: 0, insName: "PMI", minDownPct: 0 };
}

export const closingCosts = (price: number, pct: number) => cents((price * Math.max(0, pct)) / 100);
export const cashToClose = (downAmt: number, closing: number) => cents(downAmt + closing);

/** Monthly P&I and total interest for one term, with no extra payments. Insurance is not part of P&I. */
export function termSummary(type: LoanType, price: number, downAmt: number, annualPct: number, years: number, vaUse: VaUse = "first") {
  const t = programTerms(type, price, downAmt, years, vaUse);
  const r = amortize({ principal: t.loan, annualPct, months: Math.round(years * 12) });
  return { years, loan: t.loan, payment: r.payment, totalInterest: r.totalInterest };
}
export function compareTerms(type: LoanType, price: number, downAmt: number, annualPct: number, a = 15, b = 30, vaUse: VaUse = "first") {
  const x = termSummary(type, price, downAmt, annualPct, a, vaUse), y = termSummary(type, price, downAmt, annualPct, b, vaUse);
  return { short: x, long: y, interestDifference: y.totalInterest - x.totalInterest, paymentDifference: x.payment - y.payment };
}

export interface IncomeCheck { ratio: number; withinFront: boolean; maxHousing28: number; maxTotalDebt36: number; roomForOtherDebt: number }
/** Housing ratio against the 28% guideline and the 36% total-debt guideline. Null for missing or non-positive income. */
export function incomeCheck(grossMonthlyIncome: number | null, housingMonthly: number): IncomeCheck | null {
  if (grossMonthlyIncome == null || !(grossMonthlyIncome > 0)) return null;
  const ratio = (housingMonthly / grossMonthlyIncome) * 100;
  const maxHousing28 = grossMonthlyIncome * 0.28, maxTotalDebt36 = grossMonthlyIncome * 0.36;
  return { ratio, withinFront: ratio <= 28, maxHousing28, maxTotalDebt36, roomForOtherDebt: Math.max(0, maxTotalDebt36 - housingMonthly) };
}

export interface YearRow { year: number; principal: number; interest: number; balance: number }
export function yearlyRows(rows: { month: number; principal: number; interest: number; extra: number; balance: number }[]): YearRow[] {
  const out: YearRow[] = [];
  for (const r of rows) {
    const y = Math.ceil(r.month / 12);
    if (!out[y - 1]) out[y - 1] = { year: y, principal: 0, interest: 0, balance: 0 };
    out[y - 1].principal += r.principal + r.extra; out[y - 1].interest += r.interest; out[y - 1].balance = r.balance;
  }
  return out;
}

/** Balance at the start (index 0, year 0) and end of each year, for the chart. */
export function balanceSeries(principal: number, yearly: YearRow[]): { year: number; balance: number }[] {
  return [{ year: 0, balance: principal }, ...yearly.map(y => ({ year: y.year, balance: y.balance }))];
}

export function formatMonths(n: number): string {
  const y = Math.floor(n / 12), m = n % 12;
  return [y ? `${y} year${y > 1 ? "s" : ""}` : "", m ? `${m} month${m > 1 ? "s" : ""}` : ""].filter(Boolean).join(" ") || "0 months";
}

/* ---------- shareable URL ---------- */
export interface MortgageInputs {
  price: string; down: string; downUnit: "pct" | "usd"; rate: string; years: string; tax: string; taxUnit: "pct" | "year" | "month";
  insurance: string; hoa: string; pmiRate: string; loanType: LoanType; vaUse: VaUse; extraMonthly: string; oneTime: string; oneTimeMonth: string; closingPct: string; income: string;
  /** First payment date as YYYY-MM-DD; empty means the default (the 1st of next month). It only labels the schedule. */
  firstPayment: string;
  /** Other monthly debt payments (car, cards, student loans) for the back-end ratio. */
  debts: string;
  /** Yearly increase in percent for property tax, insurance and HOA, and the monthly maintenance/other cost. Empty means 0. */
  taxGrowth: string; insGrowth: string; hoaGrowth: string; other: string;
  /** One extra payment each year: the amount and the loan payment number (1 to 12) it is made in. */
  yearlyExtra: string; yearlyExtraMonth: string;
}
export const DEFAULT_INPUTS: MortgageInputs = {
  price: "400000", down: "20", downUnit: "pct", rate: "6.5", years: "30", tax: "1.1", taxUnit: "pct", insurance: "100", hoa: "0", pmiRate: "0.6",
  loanType: "conventional", vaUse: "first", extraMonthly: "", oneTime: "", oneTimeMonth: "1", closingPct: "3", income: "", firstPayment: "",
  debts: "", taxGrowth: "", insGrowth: "", hoaGrowth: "", other: "", yearlyExtra: "", yearlyExtraMonth: "12",
};
const KEYS: Record<keyof MortgageInputs, string> = {
  price: "price", down: "down", downUnit: "du", rate: "rate", years: "yrs", tax: "tax", taxUnit: "tu", insurance: "ins", hoa: "hoa", pmiRate: "pmi",
  loanType: "type", vaUse: "vau", extraMonthly: "xm", oneTime: "x1", oneTimeMonth: "x1m", closingPct: "cc", income: "inc", firstPayment: "fpd",
  debts: "debts", taxGrowth: "tg", insGrowth: "ig", hoaGrowth: "hg", other: "oth", yearlyExtra: "xy", yearlyExtraMonth: "xym",
};
const NUMERIC = /^[\d.,\s$%-]{0,14}$/;
const ENUMS: Partial<Record<keyof MortgageInputs, string[]>> = { downUnit: ["pct", "usd"], taxUnit: ["pct", "year", "month"], loanType: ["conventional", "fha", "va", "usda"], vaUse: ["first", "subsequent", "exempt"] };

/** Only keeps values that differ from the defaults, so links stay short. */
export function toQuery(i: MortgageInputs): string {
  const p = new URLSearchParams();
  (Object.keys(KEYS) as (keyof MortgageInputs)[]).forEach(k => { if (i[k] !== DEFAULT_INPUTS[k]) p.set(KEYS[k], i[k]); });
  return p.toString();
}
export function fromQuery(search: string): MortgageInputs {
  const p = new URLSearchParams(search);
  const out: MortgageInputs = { ...DEFAULT_INPUTS };
  (Object.keys(KEYS) as (keyof MortgageInputs)[]).forEach(k => {
    const v = p.get(KEYS[k]);
    if (v == null) return;
    const allowed = ENUMS[k];
    if (k === "firstPayment") { if (parsePaymentDate(v)) out.firstPayment = v; }
    else if (allowed) { if (allowed.includes(v)) (out as unknown as Record<string, string>)[k] = v; }
    else if (NUMERIC.test(v)) (out as unknown as Record<string, string>)[k] = v;
  });
  return out;
}
